import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Register = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') === 'broker' ? 'broker' : 'buyer';
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', role: defaultRole });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/register', form);
      // Auto-login
      const { data: loginData } = await api.post('/auth/login', { email: form.email, password: form.password });
      login(loginData.access_token, loginData.user);
      toast.success('Account created successfully!');
      if (loginData.user.role === 'broker') {
        navigate('/broker/kyc');
      } else {
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img src="https://images.unsplash.com/photo-1570124748133-fc12a93e3653?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2C59]/95 to-[#0F2C59]/70" />
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-11 h-11 rounded-md bg-[#F97316] flex items-center justify-center text-white font-black text-xl">P</div>
            <span className="font-display font-extrabold text-2xl">PropertyByKilo</span>
          </Link>
          <h1 className="font-display font-extrabold text-4xl leading-tight tracking-tight">Join India's premier<br />real estate ecosystem.</h1>
          <p className="mt-4 text-slate-200 max-w-md">Whether you're a buyer or a broker — start your journey today.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-[#0F2C59] flex items-center justify-center text-white font-black">P</div>
            <span className="font-display font-extrabold text-lg text-[#0F2C59]">PropertyByKilo</span>
          </Link>

          <h2 className="font-display font-bold text-3xl text-slate-900" data-testid="register-title">Create Account</h2>
          <p className="text-slate-500 text-sm mt-1">Get started with PropertyByKilo</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">I am a</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1 h-11" data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Property Buyer</SelectItem>
                  <SelectItem value="broker">Real Estate Broker</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 h-11" data-testid="register-name-input" />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 h-11" data-testid="register-email-input" />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Mobile</Label>
              <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" className="mt-1 h-11" data-testid="register-phone-input" />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} className="mt-1 h-11" data-testid="register-password-input" />
            </div>

            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] font-semibold" data-testid="register-submit-btn">
              {loading ? 'Creating...' : form.role === 'broker' ? 'Register as Broker' : 'Create Account'}
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            Already have an account? <Link to="/login" className="font-semibold text-[#F97316]" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
